import React, { FC } from 'react';
import classnames from 'classnames';
import Svg from 'svg-inline-react';
import { Avatar } from '@src/components/Avatar';
import { ActionMenuPopover } from '@src/components/Popover';
import { LinkedIn } from '@src/components/LinkedIn';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { getNameInitials } from '@src/util/getNameInitials';
import { parseLinkedIn } from '@src/util/parseLinkedIn';
import { trackEvent } from '@src/metrics';
import { useApplicationForCandidateProfileHeaderQuery, UserProfileLinkTypeEnum } from '@src/graphql/generated';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { useAppDispatch } from '@src/redux';
import CloseSvg from '@src/svgs/close.svg';
import { CandidateProfileHeaderSkeleton } from '../CandidateProfileHeaderSkeleton';
import { CandidateProfileHeaderWithApplicationProps } from './props';

export const CandidateProfileHeaderWithApplication: FC<CandidateProfileHeaderWithApplicationProps> = ({
  className,
  actions,
  openingId,
  applicationId,
  metricsContext,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const {
    loading: isCandidateProfileHeaderLoading,
    error: candidateProfileHeaderError,
    data: candidateProfileHeaderData,
  } = useApplicationForCandidateProfileHeaderQuery({
    fetchPolicy: 'network-only',
    onError: () => dispatch(setErrorFlashMessage()),
    variables: { id: applicationId },
  });

  const wrapperClassName = classnames('candidate-profile-header', className);

  if (isCandidateProfileHeaderLoading) {
    return (
      <div className={wrapperClassName}>
        <CandidateProfileHeaderSkeleton />
      </div>
    );
  }

  if (candidateProfileHeaderError) {
    return null;
  }

  const user = candidateProfileHeaderData?.application.user;
  const name = user?.name;
  const position = user?.position;
  const avatarColor = user?.avatarColor;
  const locationShortName = user?.location?.shortName;
  const links = user?.profile.links;

  const linkedinLink = links?.find(link => link.type === UserProfileLinkTypeEnum.LINKEDIN);

  const parsedLinkedIn = linkedinLink ? parseLinkedIn(linkedinLink.value) : undefined;
  const linkedInUrl = parsedLinkedIn?.url;

  return (
    <div className={wrapperClassName}>
      <div className="candidate-profile-header__actions">
        {!!actions.length && <ActionMenuPopover actions={actions} onAction={item => actions[item.index].action()} />}

        {onClose && <Svg className="candidate-profile-header__close-btn" onClick={onClose} src={CloseSvg} />}
      </div>

      <div className="candidate-profile-header__user-wrapper">
        <Avatar avatarColor={avatarColor} initials={getNameInitials(name)} size="xx-large" />

        <div className="candidate-profile-header__user">
          <div className="candidate-profile-header__container">
            <div className="candidate-profile-header__name" title={name || undefined}>
              {name}
            </div>

            <LinkedIn
              linkedInUrl={linkedInUrl}
              onClick={() =>
                trackEvent({
                  context: MetricContext.CANDIDATE_MANAGEMENT,
                  interaction: MetricAction.OPEN_LINKEDIN,
                  payload: {
                    'Application Id': applicationId,
                    'Event Source': MetricEventSource.CANDIDATE_PROFILE_LINKEDIN_ICON,
                    'Opening Id': openingId,
                    'Previous Context': metricsContext,
                  },
                })
              }
              wrapperClassName="candidate-profile-header__linkedin"
            />
          </div>

          <div className="candidate-profile-header__sub-title">
            <span className="candidate-profile-header__position" title={position || undefined}>
              {position}
            </span>

            {locationShortName && position && <span className="candidate-profile-header__dot" />}
            <span className="candidate-profile-header__location" title={locationShortName}>
              {locationShortName}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
